import { getSpeechToken, getSpeechTokenInternal } from "apps/diagnostics/api";
import { ConstantValues } from "common/constants";
import * as sdk from "microsoft-cognitiveservices-speech-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ISpeechState,
  IUseAzureSpeechProps,
  Kit,
} from "./useAzureSpeech.types";

export function useAzureSpeech(props: IUseAzureSpeechProps) {
  const audioRef = useRef<HTMLAudioElement>(null);

  const speechKitRef = useRef<Kit | null>(null);
  const lastSpeechTimeRef = useRef<number>(0);
  const onSilenceDetectedRef = useRef(props.onSilenceDetected);
  const isSpeakingRef = useRef<boolean>(false);
  const speakResolveRef = useRef<(() => void) | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const isDisposedRef = useRef<boolean>(false);
  const shouldPlayRef = useRef<boolean>(true);
  const isListeningRef = useRef<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [ready, setReady] = useState<boolean>(false);
  const [speaking, setSpeaking] = useState<boolean>(false);
  const [listening, setListening] = useState<boolean>(false);
  const [isDetectingSpeech, setIsDetectingSpeech] = useState<boolean>(false);
  const [silenceTimer, setSilenceTimer] = useState<number>(3);

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [partial, setPartial] = useState<string>(ConstantValues.EMPTY_STRING);
  const [transcript, setTranscript] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [error, setError] = useState<string>(ConstantValues.EMPTY_STRING);

  const enumerateMics = useCallback(async () => {
    try {
      const deviceList = await navigator.mediaDevices.enumerateDevices();
      setDevices(deviceList.filter((d) => d.kind === "audioinput"));
    } catch {
      setDevices([]);
    }
  }, []);

  const init = useCallback(async () => {
    if (!props.enabled) {
      return;
    }

    isDisposedRef.current = false;

    try {
      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch {
      setError("Microphone permission denied.");
      setReady(false);
      return;
    }

    try {
      setLoading(true);
      let region = null;
      let token = null;

      if (props.diagnosticId) {
        const res = await getSpeechTokenInternal(
          props.diagnosticId,
          props.distributionId,
        );
        token = res.token;
        region = res.region;
      } else {
        const res = await getSpeechToken();
        token = res.token;
        region = res.region;
      }
      const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(
        token,
        region,
      );
      speechConfig.speechRecognitionLanguage = props.locale;
      speechConfig.speechSynthesisVoiceName =
        props.voiceName || "en-US-JennyMultilingualNeural";
      speechConfig.setProperty(
        sdk.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs,
        "3300",
      );

      const stream = sdk.AudioOutputStream.createPullStream();
      const audioOut = sdk.AudioConfig.fromStreamOutput(stream);
      const audioIn = sdk.AudioConfig.fromStreamInput(mediaStreamRef.current);

      const synthesizer = new sdk.SpeechSynthesizer(speechConfig, audioOut);
      const recognizer = new sdk.SpeechRecognizer(speechConfig, audioIn);

      speechKitRef.current = { speechConfig, synthesizer, recognizer };
      setPartial(ConstantValues.EMPTY_STRING);
      setTranscript(ConstantValues.EMPTY_STRING);
      setError(ConstantValues.EMPTY_STRING);
      setReady(true);
      setLoading(false);
    } catch (e) {
      console.error("Azure Speech init failed", e);
      setError("Failed to initialize speech.");
      setReady(false);
      setLoading(false);
    }
  }, [props.enabled, props.locale, props.deviceId, enumerateMics]);

  const dispose = useCallback(() => {
    isDisposedRef.current = true;

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    const k = speechKitRef.current;

    if (k?.recognizer) {
      k.recognizer.speechStartDetected = () => {};
      k.recognizer.speechEndDetected = () => {};
      k.recognizer.recognizing = () => {};
      k.recognizer.recognized = () => {};
      k.recognizer.canceled = () => {};
      k.recognizer.sessionStopped = () => {};

      k.recognizer.stopContinuousRecognitionAsync(
        () => {
          console.log("Recognition stopped, closing recognizer");
          k.recognizer.close();
        },
        (err) => {
          console.error("Error stopping recognition:", err);
          k.recognizer.close();
        },
      );
    }

    if (k?.synthesizer) {
      k.synthesizer.close();
    }

    speechKitRef.current = null;

    setReady(false);
    setListening(false);
    setSpeaking(false);
    setIsDetectingSpeech(false);
  }, []);

  useEffect(() => {
    onSilenceDetectedRef.current = props.onSilenceDetected;
  }, [props.onSilenceDetected]);

  useEffect(() => {
    isListeningRef.current = listening;
  }, [listening]);

  useEffect((): void => {
    if (props.enabled) {
      dispose();
      init();
      return;
    }

    dispose();
  }, [props.enabled, props.locale, props.deviceId]);

  useEffect(() => {
    const handler = () => enumerateMics();
    navigator.mediaDevices?.addEventListener?.("devicechange", handler);
    return () =>
      navigator.mediaDevices?.removeEventListener?.("devicechange", handler);
  }, [enumerateMics]);

  const pauseSpeaking = useCallback(() => {
    shouldPlayRef.current = false;
    if (audioRef.current) {
      audioRef.current.pause();
    }
    isSpeakingRef.current = false;
    setSpeaking(false);
    if (speakResolveRef.current) {
      speakResolveRef.current();
      speakResolveRef.current = null;
    }
  }, []);

  const restartSpeaking = useCallback(() => {
    if (audioRef.current) {
      setSpeaking(true);
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
  }, []);

  const stopSpeaking = useCallback(() => {
    try {
      const kit = speechKitRef.current;
      if (kit?.synthesizer) {
        kit.synthesizer.close();

        const stream = sdk.AudioOutputStream.createPullStream();
        const audioConfig = sdk.AudioConfig.fromStreamOutput(stream);
        kit.synthesizer = new sdk.SpeechSynthesizer(
          kit.speechConfig,
          audioConfig,
        );
      }

      if (speakResolveRef.current) {
        speakResolveRef.current();
        speakResolveRef.current = null;
      }

      isSpeakingRef.current = false;
      setSpeaking(false);
    } catch (e) {
      console.error("Error in stopSpeaking:", e);
      isSpeakingRef.current = false;
      setSpeaking(false);
    }
  }, []);

  const speak = useCallback(
    async (text: string | undefined) => {
      if (isSpeakingRef.current) {
        stopSpeaking();
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const kit: Kit | null = speechKitRef.current;
      if (!kit) {
        throw new Error("Speech synthesizer not ready");
      }

      if (!kit.synthesizer) {
        const stream = sdk.AudioOutputStream.createPullStream();
        const audioConfig = sdk.AudioConfig.fromStreamOutput(stream);
        kit.synthesizer = new sdk.SpeechSynthesizer(
          kit.speechConfig,
          audioConfig,
        );
      }

      isSpeakingRef.current = true;
      shouldPlayRef.current = true;
      setSpeaking(true);

      return new Promise<void>((resolve) => {
        speakResolveRef.current = resolve;

        try {
          kit.synthesizer.speakTextAsync(
            text || ConstantValues.EMPTY_STRING,
            (result) => {
              if (
                result.reason === sdk.ResultReason.SynthesizingAudioCompleted
              ) {
                const blob = new Blob([result.audioData], {
                  type: "audio/wav",
                });
                const url = URL.createObjectURL(blob);

                setTimeout(() => {
                  if (audioRef.current && shouldPlayRef.current) {
                    audioRef.current.src = url;
                    audioRef.current.play();
                  }
                }, 100);
              }
            },
            (err) => {
              console.error("Speech synthesis error:", err);
              speakResolveRef.current = null;
              isSpeakingRef.current = false;
              setSpeaking(false);
              resolve();
            },
          );
        } catch (err) {
          console.error("Error starting speech:", err);
          speakResolveRef.current = null;
          isSpeakingRef.current = false;
          setSpeaking(false);
          resolve();
        }
      });
    },
    [stopSpeaking],
  );

  const onAudioEnded = useCallback(() => {
    setSpeaking(false);
    isSpeakingRef.current = false;
    if (speakResolveRef.current) {
      speakResolveRef.current();
      speakResolveRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    const kit = speechKitRef.current;
    if (!kit || isListeningRef.current) {
      return;
    }

    setTranscript(ConstantValues.EMPTY_STRING);
    setPartial(ConstantValues.EMPTY_STRING);
    setError(ConstantValues.EMPTY_STRING);
    setListening(true);
    setIsDetectingSpeech(false);

    let buffer: string = ConstantValues.EMPTY_STRING;

    kit.recognizer.speechStartDetected = (
      _: any,
      _e: sdk.RecognitionEventArgs,
    ) => {
      if (isDisposedRef.current || speaking) return;
      console.log("Speech start detected");
      setIsDetectingSpeech(true);
      lastSpeechTimeRef.current = Date.now();
    };

    kit.recognizer.speechEndDetected = (
      _: any,
      _e: sdk.RecognitionEventArgs,
    ) => {
      if (isDisposedRef.current || speaking) return;
      console.log("Speech end detected");

      const finalText = buffer.trim();
      if (finalText && onSilenceDetectedRef.current) {
        onSilenceDetectedRef.current(finalText);
        buffer = ConstantValues.EMPTY_STRING;
        setTranscript(ConstantValues.EMPTY_STRING);
        setPartial(ConstantValues.EMPTY_STRING);
        setIsDetectingSpeech(false);
      }
    };

    kit.recognizer.recognizing = (
      _: any,
      e: sdk.SpeechRecognitionEventArgs,
    ) => {
      if (isDisposedRef.current || speaking) return;
      const t: string = e.result?.text ?? ConstantValues.EMPTY_STRING;
      if (t && t.trim()) {
        setPartial(t);
        setIsDetectingSpeech(true);
        lastSpeechTimeRef.current = Date.now();
      }
    };

    kit.recognizer.recognized = (_: any, e: sdk.SpeechRecognitionEventArgs) => {
      if (isDisposedRef.current || speaking) return;

      if (e.result?.reason === sdk.ResultReason.RecognizedSpeech) {
        const seg = e.result.text ?? ConstantValues.EMPTY_STRING;
        if (seg) {
          if (isSpeakingRef.current === true || speaking) pauseSpeaking();

          buffer = (buffer + " " + seg).trim();
          setTranscript(buffer);
          setPartial(ConstantValues.EMPTY_STRING);
          setIsDetectingSpeech(true);
          lastSpeechTimeRef.current = Date.now();
        }
      } else if (e.result?.reason === sdk.ResultReason.NoMatch) {
        setIsDetectingSpeech(false);
      }
    };

    kit.recognizer.canceled = (
      _: any,
      e: sdk.SpeechRecognitionCanceledEventArgs,
    ) => {
      setError(e.errorDetails || "Recognition canceled.");
      setListening(false);
    };

    kit.recognizer.sessionStopped = () => {
      setListening(false);
      setPartial(ConstantValues.EMPTY_STRING);
    };

    kit.recognizer.startContinuousRecognitionAsync(
      () => {},
      (err) => {
        console.error("startContinuousRecognitionAsync error:", err);
        setListening(false);
      },
    );
  }, [stopSpeaking]);

  const stop = useCallback(() => {
    const kit = speechKitRef.current;
    if (!kit) {
      return;
    }
    setIsDetectingSpeech(false);

    kit.recognizer.speechStartDetected = () => {};
    kit.recognizer.speechEndDetected = () => {};
    kit.recognizer.recognizing = () => {};
    kit.recognizer.recognized = () => {};

    kit.recognizer.stopContinuousRecognitionAsync(
      () => setListening(false),
      () => setListening(false),
    );
  }, []);

  useEffect(() => {
    if (!listening) {
      setSilenceTimer(3);
      return;
    }
    const interval = setInterval(() => {
      if (lastSpeechTimeRef.current === 0) return;
      const elapsed = Date.now() - lastSpeechTimeRef.current;
      setSilenceTimer(Math.max(0, Math.ceil((3000 - elapsed) / 1000)));
    }, 200);
    return () => clearInterval(interval);
  }, [listening]);

  const clear = useCallback(() => {
    stop();
    setTranscript(ConstantValues.EMPTY_STRING);
    setPartial(ConstantValues.EMPTY_STRING);
    setError(ConstantValues.EMPTY_STRING);
    setIsDetectingSpeech(false);

    if (speechKitRef.current) {
      speechKitRef.current.recognizer.stopContinuousRecognitionAsync(
        () => setListening(false),
        () => setListening(false),
      );
      speechKitRef.current = null;
    }
  }, [stop]);

  return useMemo<ISpeechState>(
    () => ({
      ready,
      loading,
      speaking,
      listening,
      partial,
      transcript,
      error,
      devices,
      isDetectingSpeech,
      silenceTimer,

      speak,
      start,
      stop,
      stopSpeaking,
      clear,
      dispose,
      pauseSpeaking,
      restartSpeaking,
      reinit: init,
      audioElement: <audio ref={audioRef} onEnded={onAudioEnded} hidden />,
    }),
    [
      ready,
      loading,
      speaking,
      listening,
      partial,
      transcript,
      error,
      devices,
      isDetectingSpeech,
      silenceTimer,
      speak,
      start,
      stop,
      stopSpeaking,
      clear,
      dispose,
      init,
      onAudioEnded,
      pauseSpeaking,
      restartSpeaking,
    ],
  );
}
